// Types pour les données
export interface Club {
  id: string;
  nomClub: string;
  nomMaitre: string;
  prenomMaitre: string;
  grade: string;
  telephone: string;
  ville: string;
  quartier: string;
  montantAdhesion?: number;
  statut: 'en_attente' | 'approuve' | 'rejete';
  dateCreation: Date;
}

export interface CreateClubRequest {
  nomClub: string;
  nomMaitre: string;
  prenomMaitre: string;
  grade: string;
  telephone: string;
  ville: string;
  quartier: string;
}

export interface Athlete {
  id: string;
  nom: string;
  prenom: string;
  dateNaissance: string;
  situationMatrimoniale: string;
  situationGeographique: string;
  quartier: string;
  civilite: string;
  telephone: string;
  grade: string;
  contactUrgence: {
    nom: string;
    prenom: string;
    telephone: string;
  };
  clubPreference: string;
  clubSouhaite: string;
  nomMaitre: string;
  telephoneMaitre: string;
  montantAdhesion?: number;
  statut: 'en_attente' | 'approuve' | 'rejete';
  dateCreation: Date;
}

export interface CreateAthleteRequest {
  nom: string;
  prenom: string;
  dateNaissance: string;
  situationMatrimoniale: string;
  situationGeographique: string;
  quartier: string;
  civilite: string;
  telephone: string;
  grade: string;
  contactUrgence: {
    nom: string;
    prenom: string;
    telephone: string;
  };
  clubPreference: string;
  clubSouhaite: string;
  nomMaitre: string;
  telephoneMaitre: string;
}

export interface Admin {
  id: string;
  email: string;
  nom: string;
  prenom: string;
  telephone?: string;
  role: 'admin' | 'super_admin';
  dateCreation: Date;
}

// Services API
const API_BASE_URL = 'https://aikash.ci/api';

// Service pour les clubs
export const clubService = {
  async getAll(): Promise<Club[]> {
    const response = await fetch(`${API_BASE_URL}/clubs`);
    if (!response.ok) {
      throw new Error('Erreur lors de la récupération des clubs');
    }
    const data = await response.json();
    return data.data;
  },

  async create(clubData: CreateClubRequest): Promise<Club> {
    const response = await fetch(`${API_BASE_URL}/clubs`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(clubData),
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Erreur lors de la création du club');
    }
    const data = await response.json();
    return data.data;
  },

  async update(id: string, clubData: CreateClubRequest): Promise<Club> {
    const response = await fetch(`${API_BASE_URL}/clubs/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(clubData),
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || errorData.error || 'Erreur lors de la mise à jour du club');
    }
    const data = await response.json();
    return data.data;
  },

  async getPending(): Promise<Club[]> {
    const response = await fetch(`${API_BASE_URL}/clubs/admin/pending`);
    if (!response.ok) {
      throw new Error('Erreur lors de la récupération des clubs en attente');
    }
    const data = await response.json();
    return data.data;
  },

  async approve(id: string, montantAdhesion: number): Promise<Club> {
    const response = await fetch(`${API_BASE_URL}/clubs/admin/${id}/approve`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ montantAdhesion }),
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Erreur lors de l\'approbation du club');
    }
    const data = await response.json();
    return data.data;
  },

  async reject(id: string): Promise<Club> {
    const response = await fetch(`${API_BASE_URL}/clubs/admin/${id}/reject`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Erreur lors du rejet du club');
    }
    const data = await response.json();
    return data.data;
  },

  async delete(id: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/clubs/${id}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Erreur lors de la suppression du club');
    }
  },
};

// Service pour les athlètes
export const athleteService = {
  async getAll(): Promise<Athlete[]> {
    const response = await fetch(`${API_BASE_URL}/athletes`);
    if (!response.ok) {
      throw new Error('Erreur lors de la récupération des athlètes');
    }
    const data = await response.json();
    return data.data;
  },

  async create(athleteData: CreateAthleteRequest): Promise<Athlete> {
    const response = await fetch(`${API_BASE_URL}/athletes`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(athleteData),
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Erreur lors de la création de l\'athlète');
    }
    const data = await response.json();
    return data.data;
  },

  async getPending(): Promise<Athlete[]> {
    const response = await fetch(`${API_BASE_URL}/athletes/admin/pending`);
    if (!response.ok) {
      throw new Error('Erreur lors de la récupération des athlètes en attente');
    }
    const data = await response.json();
    return data.data;
  },

  async approve(id: string, montantAdhesion: number): Promise<Athlete> {
    const response = await fetch(`${API_BASE_URL}/athletes/admin/${id}/approve`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ montantAdhesion }),
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Erreur lors de l\'approbation de l\'athlète');
    }
    const data = await response.json();
    return data.data;
  },

  async reject(id: string): Promise<Athlete> {
    const response = await fetch(`${API_BASE_URL}/athletes/admin/${id}/reject`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Erreur lors du rejet de l\'athlète');
    }
    const data = await response.json();
    return data.data;
  },

  async delete(id: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/athletes/${id}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Erreur lors de la suppression de l\'athlète');
    }
  },
};

// Service pour l'administration
export const adminService = {
  async login(credentials: { email: string; password: string }): Promise<Admin> {
    const response = await fetch(`${API_BASE_URL}/admin/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(credentials),
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Erreur de connexion');
    }
    const data = await response.json();
    return data.data;
  },

  async getAll(): Promise<Admin[]> {
    const response = await fetch(`${API_BASE_URL}/admin`);
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Erreur lors de la récupération des administrateurs');
    }
    const data = await response.json();
    return data.data;
  },

  async create(adminData: {
    email: string;
    password: string;
    nom: string;
    prenom: string;
    telephone: string;
    dateNaissance?: string;
    ville?: string;
    civilite?: string;
    role?: 'admin' | 'super_admin';
  }): Promise<Admin> {
    const response = await fetch(`${API_BASE_URL}/admin`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(adminData),
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Erreur lors de la création de l\'administrateur');
    }
    const data = await response.json();
    return data.data;
  },

  async delete(id: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/admin/${id}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Erreur lors de la suppression de l\'administrateur');
    }
  },

  async resetPassword(id: string, newPassword: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/admin/${id}/reset-password`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ password: newPassword }),
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Erreur lors de la réinitialisation du mot de passe');
    }
  },
};

// Service pour les vidéos
export const videoService = {
  // Récupérer toutes les vidéos depuis la base de données
  async getAllVideos() {
    try {
      const response = await fetch(`${API_BASE_URL}/videos`);
      if (!response.ok) {
        throw new Error('Erreur lors de la récupération des vidéos');
      }
      return await response.json();
    } catch (error) {
      console.error('Erreur API getAllVideos:', error);
      return [];
    }
  },

  // Ajouter une nouvelle vidéo avec upload de fichier
  async addVideo(videoFile: File, videoData: {
    title: string;
    description: string;
    duration?: string;
    category: string;
  }) {
    try {
      const formData = new FormData();
      formData.append('video', videoFile);
      formData.append('title', videoData.title);
      formData.append('description', videoData.description);
      formData.append('duration', videoData.duration || '00:00');
      formData.append('category', videoData.category);

      const response = await fetch(`${API_BASE_URL}/videos`, {
        method: 'POST',
        body: formData,
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Erreur lors de l\'ajout de la vidéo');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Erreur API addVideo:', error);
      throw error;
    }
  },

  // Supprimer une vidéo
  async deleteVideo(id: string) {
    try {
      const response = await fetch(`${API_BASE_URL}/videos/${id}`, {
        method: 'DELETE',
      });
      
      if (!response.ok) {
        throw new Error('Erreur lors de la suppression de la vidéo');
      }
      
      return true;
    } catch (error) {
      console.error('Erreur API deleteVideo:', error);
      throw error;
    }
  },

  // Mettre à jour une vidéo
  async updateVideo(id: string, videoFile?: File, videoData?: {
    title?: string;
    description?: string;
    duration?: string;
    category?: string;
  }) {
    try {
      const formData = new FormData();
      
      if (videoFile) {
        formData.append('video', videoFile);
      }
      
      if (videoData) {
        if (videoData.title) formData.append('title', videoData.title);
        if (videoData.description) formData.append('description', videoData.description);
        if (videoData.duration) formData.append('duration', videoData.duration);
        if (videoData.category) formData.append('category', videoData.category);
      }

      const response = await fetch(`${API_BASE_URL}/videos/${id}`, {
        method: 'PUT',
        body: formData,
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Erreur lors de la mise à jour de la vidéo');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Erreur API updateVideo:', error);
      throw error;
    }
  }
};

// Service pour les images
export const imageService = {
  async getAllImages() {
    try {
      const response = await fetch(`${API_BASE_URL}/images`);
      if (!response.ok) {
        throw new Error('Erreur lors de la récupération des images');
      }
      return await response.json();
    } catch (error) {
      console.error('Erreur API getAllImages:', error);
      return [];
    }
  },

  // Ajouter une nouvelle image avec upload de fichier
  async addImage(imageFile: File, imageData: {
    title: string;
    description: string;
    category: string;
  }) {
    try {
      const formData = new FormData();
      formData.append('image', imageFile);
      formData.append('title', imageData.title);
      formData.append('description', imageData.description);
      formData.append('category', imageData.category);

      const response = await fetch(`${API_BASE_URL}/images`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Erreur lors de l\'ajout de l\'image');
      }

      return await response.json();
    } catch (error) {
      console.error('Erreur API addImage:', error);
      throw error;
    }
  },

  async deleteImage(id: string) {
    try {
      const response = await fetch(`${API_BASE_URL}/images/${id}`, {
        method: 'DELETE',
      });
      if (!response.ok) {
        throw new Error('Erreur lors de la suppression de l\'image');
      }
      return true;
    } catch (error) {
      console.error('Erreur API deleteImage:', error);
      throw error;
    }
  },

  // Mettre à jour une image
  async updateImage(id: string, imageFile?: File, imageData?: {
    title?: string;
    description?: string;
    category?: string;
  }) {
    try {
      const formData = new FormData();
      if (imageFile) {
        formData.append('image', imageFile);
      }
      if (imageData) {
        if (imageData.title) formData.append('title', imageData.title);
        if (imageData.description) formData.append('description', imageData.description);
        if (imageData.category) formData.append('category', imageData.category);
      }

      const response = await fetch(`${API_BASE_URL}/images/${id}`, {
        method: 'PUT',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Erreur lors de la mise à jour de l\'image');
      }

      return await response.json();
    } catch (error) {
      console.error('Erreur API updateImage:', error);
      throw error;
    }
  }
};

// Service pour les événements
export const eventService = {
  // Récupérer tous les événements
  async getAllEvents() {
    try {
      const response = await fetch(`${API_BASE_URL}/events`);
      if (!response.ok) {
        throw new Error('Erreur lors de la récupération des événements');
      }
      return await response.json();
    } catch (error) {
      console.error('Erreur service événements:', error);
      throw error;
    }
  },

  // Créer un nouvel événement
  async createEvent(eventData: {
    titre: string;
    description: string;
    dateDebut: string;
    heureDebut: string;
    dateFin: string;
    heureFin: string;
    lieu: string;
    type: string;
  }) {
    try {
      const response = await fetch(`${API_BASE_URL}/events`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(eventData),
      });
      
      if (!response.ok) {
        throw new Error('Erreur lors de la création de l\'événement');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Erreur création événement:', error);
      throw error;
    }
  },

  // Mettre à jour un événement
  async updateEvent(id: string, eventData: any) {
    try {
      const response = await fetch(`${API_BASE_URL}/events/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(eventData),
      });
      
      if (!response.ok) {
        throw new Error('Erreur lors de la mise à jour de l\'événement');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Erreur mise à jour événement:', error);
      throw error;
    }
  },

  // Supprimer un événement
  async deleteEvent(id: string) {
    try {
      const response = await fetch(`${API_BASE_URL}/events/${id}`, {
        method: 'DELETE',
      });
      
      if (!response.ok) {
        throw new Error('Erreur lors de la suppression de l\'événement');
      }
      
      return true;
    } catch (error) {
      console.error('Erreur suppression événement:', error);
      throw error;
    }
  }
};

export default {
  clubService,
  athleteService,
  adminService,
  videoService,
  imageService,
  eventService
};
